import * as React from "react";
import clsx from "clsx";

import { Button, message, Spin, Typography } from "antd";
import { CSSTransition } from "react-transition-group";
import { ReactNode, useEffect, useState } from "react";
import CreateNewStep from "./CreateNewStep";
import PPTSlidesPreview from "./PPTSlidesPreview";
import FontsStep from "./FontsStep";
import ColorsStep from "./ColorsStep";
import NumericalSelectionStep from "./NumericalSelectionStep";
import EditApprovalSection from "./EditApprovalSection";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useFormatTemplateStore } from "@/state/formatTemplateStore";
import { createFormatTemplate } from "@/utils/formatTemplate/requestHelper";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { formatTemplateLabels, formatTemplateStepSize } from "@/utils/alphaAgent/helper";
import { httpClient } from "@/utils/api/client";
import { logger } from "@/helpers/tslog";

interface StepProps {
  show: boolean;
  children: ReactNode;
}
function Step({ show, children }: StepProps) {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={show} // ← visible or hidden?
      timeout={150}
      classNames="step" // ← base for all generated class-names
      mountOnEnter={false} // keep it in the DOM from the first render
      unmountOnExit={true} // never un-mount (so state survives)
      appear={show}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="step-slot">
        {children}
      </div>
    </CSSTransition>
  );
}

export default function FormatEdit() {
  const [isLoading, setIsLoading] = useState(false);
  const [showDiv, setShowDiv] = useState(true);
  const isDefaultFormat = useFormatTemplateStore((state: { isDefaultFormat: any }) => state.isDefaultFormat);
  const { isAllowedToEdit, setIsAllowedToEdit, setIsDefaultFormat } = useFormatTemplateStore.getState();
  const {
    questionsPerStep,
    formatId: id,
    setCurrentStep,
    setDisabledButton,
    setFormatId,
    updateFormatTemplateField,
    updateFontsSection,
    updateColorsSection,
    updateNumericalSelection,
    setEditApprovals,
    setSlideImages,
    updateImageCategory,
    resetStore,
    setFormatTemplateSidebar,
  } = useFormatTemplateStore();

  useEffect(() => {
    setIsLoading(true);
    resetStore();
    setFormatId(id as string);
    const fetchIsAllowedToEdit = async () => {
      try {
        const { data } = await httpClient.get(`/format/${id}/allowed-to-edit`);

        setIsAllowedToEdit(!!data);
      } catch (error: any) {
        logger.error(error);
      }
    };
    fetchIsAllowedToEdit();
  }, [id]);

  useEffect(() => {
    const fetchFormatterTemplate = async () => {
      try {
        const { data: formatData } = await httpClient.get("/format", {
          params: { id },
        });

        if (!formatData) {
          navigate("/formats");
          return;
        }

        const [companyName, formatName] = formatData.formatName.split("-");
        updateFormatTemplateField("companyName", companyName || "");
        updateFormatTemplateField("templateName", formatName || "");

        const blob = new Blob([formatData.file.buffer], {
          type: formatData.file.mimetype,
        });
        const file = new File([blob], formatData.file.originalname, {
          type: formatData.file.mimetype,
        });

        updateFormatTemplateField("file", file ? [file] : null);

        formatData?.images.forEach((img: any) => {
          updateImageCategory(img.name, img.category || "Other");
        });

        setSlideImages(formatData.images);

        const parsedData = JSON.parse(formatData.formatData);

        const fonts = parsedData.fontsSection || {};
        Object.entries(fonts).forEach(([key, val]) => {
          updateFontsSection(key as any, val);
        });

        const colors = parsedData.colorsSection || {};
        Object.entries(colors).forEach(([key, val]) => {
          updateColorsSection(key as any, val);
        });

        const numerical = parsedData.numericalSelection || {};
        Object.entries(numerical).forEach(([key, val]) => {
          updateNumericalSelection(key as any, val);
        });

        setEditApprovals(formatData.editApprovals || []);
        setIsDefaultFormat(!!formatData.is_default);
      } catch (err: any) {
        logger.error("Error fetching data:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormatterTemplate();
  }, [id]);

  const answersByStep = useFormatTemplateStore((state: { answersByStep: any }) => state.answersByStep);
  const currentStep = useFormatTemplateStore((state: { currentStep: any }) => state.currentStep);
  const navigate = useNavigate();
  const handleNextClick = React.useCallback(async () => {
    const {
      formatId,
      formatTemplateData,
      imagesWithCategory,
      fontsSection,
      colorsSection,
      numericalSelection,
      editApprovals,
    } = useFormatTemplateStore.getState();
    const data = {
      formatName: `${formatTemplateData.companyName.trim()}-${formatTemplateData.templateName.trim()}`,
      formatType: "EXCEL",
      formatData: JSON.stringify({
        fontsSection,
        colorsSection,
        numericalSelection,
      }),
      imagesWithCategory,
      editApprovals,
      update: true,
    };
    const response = await createFormatTemplate(formatId, data);
    if (response.success) {
      setFormatTemplateSidebar("search-existing");
      message.success("Format template update successfully!");
      const { resetStore } = useFormatTemplateStore.getState();
      resetStore();
    } else {
      message.error("Failed to create format template. Please try again.");
    }
  }, [currentStep, setCurrentStep, setDisabledButton, setFormatId]);

  const handleDelete = async () => {
    try {
      const response = await httpClient.delete("/format", {
        data: { id },
      });

      if (response.data.success) {
        message.success("Format Template deleted successfully!", 1, () => {
          setFormatTemplateSidebar("search-existing");
        });
      } else {
        message.error("Something went wrong. Please contact support.");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 422) {
          message.error(error.response?.data?.message || "An error occurred while updating the format.");
        } else {
          message.error("Something went wrong. Please contact support.");
        }
      } else {
        message.error("Something went wrong. Please contact support.");
      }
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDiv(false);
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Spin size="large" tip="Loading" />
        <Typography.Text>Loading...</Typography.Text>
      </div>
    );

  return (
    <div className="flex flex-col justify-between h-full grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4 text-start bg-gray-100">
      {!isAllowedToEdit && (
        <div
          className={
            " bg-[#fff4e5] border border-solid border-[#ffa726] text-[#d84315] p-4 rounded-lg my-4 text-base text-center shadow-md -mt-2 bg-white"
          }
        >
          <p>
            <strong>You are not allowed to edit this Template.</strong> Please contact the Formatter Template owner for
            access or assistance.
          </p>
        </div>
      )}
      {showDiv && (
        <div className={"flex justify-center"}>
          <div className="text-center w-2/3 text-red-800 p-2 mb-2 rounded-lg border-2 border-red-600 shadow-md bg-white">
            <p className={"font-bold"}>Use the navigation here to view different features of the template</p>
          </div>
        </div>
      )}
      <div
        className={clsx(
          " w-full mx-auto py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336] bg-white"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={formatTemplateLabels}
          stepSizes={formatTemplateStepSize}
          allGreen={true}
          onStepClick={(stepIndex) => {
            const { setCurrentStep } = useFormatTemplateStore.getState();
            setCurrentStep(stepIndex);
          }}
        />
      </div>

      <div className={"grid h-full relative w-full text-start "}>
        <Step show={currentStep === 0}>
          <CreateNewStep />
        </Step>
        <Step show={currentStep === 1}>
          <PPTSlidesPreview />
        </Step>
        <Step show={currentStep === 2}>
          <FontsStep />
        </Step>
        <Step show={currentStep === 3}>
          <ColorsStep />
        </Step>
        <Step show={currentStep === 4}>
          <NumericalSelectionStep />
        </Step>
        <Step show={currentStep === 5}>
          <EditApprovalSection />
        </Step>
      </div>
      <div className="flex justify-end w-full px-2 pt-4  gap-2">
        <Button
          type="default"
          onClick={() => {
            const { setFormatTemplateSidebar } = useFormatTemplateStore.getState();
            setFormatTemplateSidebar("search-existing");
          }}
        >
          Back
        </Button>

        <Button
          type={"default"}
          className={`${isDefaultFormat ? "" : "!bg-red-600 hover:!bg-red-700 !text-white !border-none focus:!ring-2 focus:!ring-red-400"}
           py-2 px-4 rounded transition duration-200`}
          onClick={handleDelete}
          disabled={!isAllowedToEdit || isDefaultFormat}
        >
          Delete Template
        </Button>
        <Button type={"primary"} className={""} onClick={handleNextClick} disabled={!isAllowedToEdit}>
          Update Template
        </Button>
      </div>
    </div>
  );
}
